import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import api from '../api';
import PublisherContentTab from './PublisherContentTab';
import PublisherPayoutsTab from './PublisherPayoutsTab';

// ── Chart theme ──────────────────────────────────────────────────────────────
// Series colors validated (dataviz six checks) against the card surface
// #2A2A2A in dark mode: gold #c98500 (unlocks/earnings — the brand hue stepped
// into the dark lightness band; the raw accent #F5A623 is too light for marks),
// blue #3987e5 (clicks). Chrome stays recessive: hairline solid grid, muted ink.
const SERIES_UNLOCKS = '#c98500';
const SERIES_CLICKS = '#3987e5';
const GRID_LINE = '#3a3a38';
const AXIS_INK = '#898781';
const FEE_FILL = '#4d4b47'; // de-emphasis segment for WallPass's share

const RANGE_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const fmtUsd = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—';
const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');
const fmtDay = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// ── Pieces ───────────────────────────────────────────────────────────────────

const Card = ({ children, className = '' }) => (
  <div className={`bg-wp-surface rounded-xl border border-wp-border p-5 ${className}`}>{children}</div>
);

const SectionTitle = ({ children, sub }) => (
  <div className="mb-4">
    <h3 className="text-white text-lg font-semibold">{children}</h3>
    {sub && <p className="text-wp-muted text-sm mt-0.5">{sub}</p>}
  </div>
);

const StatTile = ({ label, value, sub }) => (
  <Card>
    <p className="text-wp-muted text-sm">{label}</p>
    <p className="text-white text-2xl font-semibold mt-1">{value}</p>
    {sub && <p className="text-wp-muted text-xs mt-1">{sub}</p>}
  </Card>
);

// Value-first tooltip rows keyed by a short stroke of the series color; text
// stays in text tokens, the colored line-key carries identity.
const ActivityTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-wp-bg border border-wp-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-wp-muted text-xs mb-1">{fmtDay(label)}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
          <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: entry.color }} />
          <span className="text-white font-semibold">{fmtInt(entry.value)}</span>
          <span className="text-wp-muted">{entry.name}</span>
        </div>
      ))}
    </div>
  );
};

const ChartLegend = () => (
  <div className="flex items-center gap-5 text-sm text-wp-muted mb-2">
    <span className="flex items-center gap-2">
      <span className="inline-block w-4 h-0.5 rounded" style={{ backgroundColor: SERIES_UNLOCKS }} />
      Unlocks
    </span>
    <span className="flex items-center gap-2">
      <span className="inline-block w-4 h-0.5 rounded" style={{ backgroundColor: SERIES_CLICKS }} />
      Click-throughs
    </span>
  </div>
);

const ActivityChart = ({ daily }) => (
  <div style={{ width: '100%', height: 280 }}>
    <ResponsiveContainer>
      <LineChart data={daily} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
        <CartesianGrid stroke={GRID_LINE} strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={fmtDay}
          tick={{ fill: AXIS_INK, fontSize: 12 }}
          axisLine={{ stroke: GRID_LINE }}
          tickLine={false}
          minTickGap={32}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fill: AXIS_INK, fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<ActivityTooltip />} cursor={{ stroke: AXIS_INK, strokeWidth: 1 }} />
        <Line
          type="monotone"
          dataKey="clicks"
          name="Click-throughs"
          stroke={SERIES_CLICKS}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, stroke: '#2A2A2A', strokeWidth: 2 }}
        />
        <Line
          type="monotone"
          dataKey="unlocks"
          name="Unlocks"
          stroke={SERIES_UNLOCKS}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, stroke: '#2A2A2A', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

// Accessible twin of the activity chart — same slice, plain table.
const ActivityTable = ({ daily }) => (
  <div className="max-h-[280px] overflow-y-auto">
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-wp-surface">
        <tr className="text-wp-muted text-left">
          <th className="py-1.5 pr-4 font-medium">Date</th>
          <th className="py-1.5 pr-4 font-medium text-right">Unlocks</th>
          <th className="py-1.5 pr-4 font-medium text-right">Clicks</th>
          <th className="py-1.5 font-medium text-right">Earnings (gross)</th>
        </tr>
      </thead>
      <tbody className="text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {[...daily].reverse().map((d) => (
          <tr key={d.date} className="border-t border-wp-border">
            <td className="py-1.5 pr-4 text-wp-muted">{fmtDay(d.date)}</td>
            <td className="py-1.5 pr-4 text-right">{fmtInt(d.unlocks)}</td>
            <td className="py-1.5 pr-4 text-right">{fmtInt(d.clicks)}</td>
            <td className="py-1.5 text-right">{fmtUsd(d.earningsUsd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Part-to-whole of exactly two: a single horizontal stacked bar (never a
// 2-slice pie). Emphasis coloring: your share in the brand hue, the platform
// fee in a de-emphasis gray. 2px surface gap between the segments.
const RevenueSplitBar = ({ summary, terms }) => {
  const feePct = terms.platformFeePercent;
  const netPct = 100 - feePct;
  const hasRevenue = summary.grossUsd > 0;
  return (
    <div>
      <div className="flex w-full h-9 rounded-lg overflow-hidden" style={{ gap: 2 }}>
        <div
          className="flex items-center px-3 min-w-0"
          style={{ width: `${netPct}%`, backgroundColor: SERIES_UNLOCKS }}
        >
          <span className="text-xs font-semibold truncate" style={{ color: '#1E1E1E' }}>
            You keep {netPct}%{hasRevenue ? ` · ${fmtUsd(summary.netUsd)}` : ''}
          </span>
        </div>
        <div
          className="flex items-center px-3 min-w-0"
          style={{ width: `${feePct}%`, backgroundColor: FEE_FILL }}
        >
          <span className="text-xs font-semibold text-white truncate">
            WallPass {feePct}%{hasRevenue ? ` · ${fmtUsd(summary.platformFeeUsd)}` : ''}
          </span>
        </div>
      </div>
      <p className="text-wp-muted text-xs mt-2">
        Gross revenue this period: <span className="text-white">{fmtUsd(summary.grossUsd)}</span> ({fmtInt(summary.tokens)}{' '}
        tokens at {fmtUsd(terms.tokenValueUsd)} each)
      </p>
    </div>
  );
};

const TopContentTable = ({ topContent }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-wp-muted text-left">
          <th className="py-2 pr-4 font-medium">Content</th>
          <th className="py-2 pr-4 font-medium text-right">Clicks</th>
          <th className="py-2 pr-4 font-medium text-right">Unlocks</th>
          <th className="py-2 pr-4 font-medium text-right">Conversion</th>
          <th className="py-2 font-medium text-right">Your earnings</th>
        </tr>
      </thead>
      <tbody className="text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {topContent.map((c) => (
          <tr key={c.postId} className="border-t border-wp-border">
            <td className="py-2 pr-4 max-w-[320px]">
              {c.url ? (
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:text-wp-accent truncate block">
                  {c.title || c.postId}
                </a>
              ) : (
                <span className="truncate block">{c.title || c.postId}</span>
              )}
            </td>
            <td className="py-2 pr-4 text-right">{fmtInt(c.clicks)}</td>
            <td className="py-2 pr-4 text-right">{fmtInt(c.unlocks)}</td>
            <td className="py-2 pr-4 text-right">
              {c.clicks > 0 ? `${Math.round((c.unlocks / c.clicks) * 100)}%` : '—'}
            </td>
            <td className="py-2 text-right">{fmtUsd(c.netUsd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RecentUnlocksTable = ({ items }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-wp-muted text-left">
          <th className="py-2 pr-4 font-medium">When</th>
          <th className="py-2 pr-4 font-medium">Content</th>
          <th className="py-2 pr-4 font-medium text-right">Tokens</th>
          <th className="py-2 font-medium text-right">Your earnings</th>
        </tr>
      </thead>
      <tbody className="text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {items.map((t) => (
          <tr key={t.id} className="border-t border-wp-border">
            <td className="py-2 pr-4 text-wp-muted whitespace-nowrap">
              {new Date(t.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </td>
            <td className="py-2 pr-4 max-w-[320px] truncate">{t.title || t.postId || t.contentUrl}</td>
            <td className="py-2 pr-4 text-right">{fmtInt(t.tokens)}</td>
            <td className="py-2 text-right">{fmtUsd(t.netUsd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── API key management (existing behavior, restyled) ────────────────────────

const ApiKeySection = ({ publisher, setPublisher }) => {
  const [newApiKey, setNewApiKey] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateKey = async () => {
    const hasKey = publisher?.hasApiKey;
    if (
      hasKey &&
      !window.confirm('Regenerate your API key? Your current key will stop working immediately and cannot be recovered.')
    ) {
      return;
    }
    setError('');
    setIsGenerating(true);
    try {
      const response = await api.post('/publisher-dashboard/me/regenerate-key');
      setNewApiKey(response.data.apiKey);
      setPublisher((prev) => ({ ...prev, hasApiKey: true, apiKeyPrefix: response.data.apiKeyPrefix }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate API key.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(''), 2000);
      },
      () => setCopySuccess('Failed to copy')
    );
  };

  const getMaskedKey = (prefix) => `${prefix}${'•'.repeat(24)}`;

  return (
    <Card>
      <SectionTitle sub="Connect your site via the WordPress plugin or the Partner API.">Your API key</SectionTitle>
      {error && <p className="text-wp-danger text-sm mb-4">{error}</p>}

      {newApiKey ? (
        <div className="bg-wp-bg border border-wp-accent/40 p-4 rounded-lg">
          <p className="text-sm font-medium text-wp-accent mb-2">
            Copy your new API key now — for security it will not be shown again.
          </p>
          <div className="flex items-center gap-2 bg-wp-surface p-3 rounded-lg border border-wp-border">
            <input
              type="text"
              value={newApiKey}
              readOnly
              className="flex-grow bg-transparent text-white font-mono text-sm border-none focus:outline-none"
            />
            <button
              onClick={() => copyToClipboard(newApiKey)}
              className="px-3 py-1 text-sm rounded-lg bg-wp-accent text-wp-bg font-semibold hover:opacity-90 transition-opacity"
            >
              {copySuccess || 'Copy'}
            </button>
          </div>
        </div>
      ) : publisher.hasApiKey ? (
        <div className="flex items-center gap-2 bg-wp-bg p-3 rounded-lg border border-wp-border">
          <input
            type="text"
            value={getMaskedKey(publisher.apiKeyPrefix || '')}
            readOnly
            className="flex-grow bg-transparent text-wp-muted font-mono text-sm border-none focus:outline-none"
            title="Only a prefix of your key is stored for display. The full key is shown only once, when generated."
          />
        </div>
      ) : (
        <p className="text-wp-muted text-sm mb-2">You don&apos;t have an API key yet.</p>
      )}

      <div className="mt-4 flex items-center gap-4">
        <button
          onClick={handleGenerateKey}
          disabled={isGenerating}
          className={`px-4 py-2 text-sm font-bold rounded-lg transition-opacity disabled:opacity-50 ${
            publisher.hasApiKey ? 'bg-wp-danger text-white hover:opacity-90' : 'bg-wp-accent text-wp-bg hover:opacity-90'
          }`}
        >
          {isGenerating ? 'Working...' : publisher.hasApiKey ? 'Regenerate Key' : 'Generate API Key'}
        </button>
        <a href="/docs" className="text-sm text-wp-accent hover:opacity-80 font-medium">
          Partner API documentation →
        </a>
      </div>
    </Card>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const PublisherDashboard = () => {
  const [publisher, setPublisher] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [days, setDays] = useState(30);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState('');
  const [activityView, setActivityView] = useState('chart');
  // Stripe returns the publisher to /publisher/dashboard?connect=return after
  // hosted onboarding — land them back on the tab they left from, not Overview.
  // Read lazily from the URL: PublisherPayoutsTab clears the param once mounted.
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get('connect') ? 'payouts' : 'overview'
  );

  useEffect(() => {
    const fetchPublisher = async () => {
      try {
        const response = await api.get('/publisher-dashboard/me');
        setPublisher(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch publisher data.');
        setIsLoading(false);
      }
    };
    fetchPublisher();
  }, []);

  const fetchStats = useCallback(async (rangeDays, initial) => {
    if (!initial) setIsRefetching(true);
    try {
      const [statsRes, txRes] = await Promise.all([
        api.get(`/publisher-dashboard/me/stats?days=${rangeDays}`),
        api.get('/publisher-dashboard/me/transactions?limit=10'),
      ]);
      setStats(statsRes.data);
      setTransactions(txRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics.');
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  }, []);

  useEffect(() => {
    if (publisher) fetchStats(days, !stats);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisher, days, fetchStats]);

  if (isLoading) {
    return <p className="text-center text-wp-muted py-16">Loading publisher dashboard…</p>;
  }
  if (error && !publisher) {
    return <p className="text-center text-wp-danger py-16">{error}</p>;
  }
  if (!publisher) {
    return <p className="text-center text-wp-muted py-16">No publisher data found.</p>;
  }

  const summary = stats?.summary;
  const terms = stats?.terms;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-white">Publisher Dashboard</h2>
        <p className="text-wp-muted mt-1">Welcome, {publisher.name}.</p>
      </div>

      {!publisher.isActive && (
        <div className="bg-wp-surface border border-wp-accent/50 text-wp-accent p-4 rounded-xl text-sm">
          Your account is <strong>pending approval</strong>. You can generate your API key now, but it will only work
          once an administrator activates your account.
        </div>
      )}

      {error && <p className="text-wp-danger text-sm">{error}</p>}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-wp-border">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'content', label: 'Content' },
          { id: 'payouts', label: 'Payouts' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${
              tab === t.id
                ? 'text-wp-accent border-wp-accent'
                : 'text-wp-muted border-transparent hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'content' && <PublisherContentTab />}

      {tab === 'payouts' && <PublisherPayoutsTab />}

      {tab === 'overview' && stats && (
        <>
          {/* Filter row — scopes every chart, stat, and table below it */}
          <div className="flex items-center gap-2">
            {RANGE_PRESETS.map((preset) => {
              const isActive = preset.days === days;
              return (
                <button
                  key={preset.days}
                  onClick={() => setDays(preset.days)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? 'bg-wp-accent text-wp-bg font-bold' : 'bg-wp-surface text-wp-muted hover:text-white'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          {/* Refetch keeps the frame: previous render held at reduced opacity */}
          <div className={`space-y-6 transition-opacity ${isRefetching ? 'opacity-60' : ''}`}>
            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile
                label="Your earnings"
                value={fmtUsd(summary.netUsd)}
                sub={`after the ${terms.platformFeePercent}% platform fee`}
              />
              <StatTile
                label="Content unlocks"
                value={fmtInt(summary.unlocks)}
                sub={`${fmtInt(summary.tokens)} tokens spent by readers`}
              />
              <StatTile
                label="Click-throughs"
                value={fmtInt(summary.clicks)}
                sub={`web ${fmtInt(summary.clicksBySource.web)} · mobile ${fmtInt(summary.clicksBySource.mobile)}${
                  summary.clicksBySource.widget ? ` · widget ${fmtInt(summary.clicksBySource.widget)}` : ''
                }`}
              />
              <StatTile
                label="Click → unlock rate"
                value={summary.conversionRate != null ? `${summary.conversionRate}%` : '—'}
                sub={`${fmtInt(summary.activeContentCount)} published items`}
              />
            </div>

            {/* Daily activity */}
            <Card>
              <div className="flex items-start justify-between">
                <SectionTitle sub="Unlocks and click-throughs per day (UTC).">Daily activity</SectionTitle>
                <button
                  onClick={() => setActivityView(activityView === 'chart' ? 'table' : 'chart')}
                  className="text-sm text-wp-muted hover:text-white transition-colors"
                >
                  {activityView === 'chart' ? 'View as table' : 'View as chart'}
                </button>
              </div>
              {activityView === 'chart' ? (
                <>
                  <ChartLegend />
                  <ActivityChart daily={stats.daily} />
                </>
              ) : (
                <ActivityTable daily={stats.daily} />
              )}
            </Card>

            {/* Revenue split + terms */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <SectionTitle sub="Exactly how this period's revenue divides.">Revenue split</SectionTitle>
                <RevenueSplitBar summary={summary} terms={terms} />
              </Card>
              <Card>
                <SectionTitle sub="No fine print — these are the numbers we run on.">Your partnership terms</SectionTitle>
                <ul className="text-sm text-white space-y-2">
                  <li className="flex justify-between gap-4">
                    <span className="text-wp-muted">Token value</span>
                    <span>{fmtUsd(terms.tokenValueUsd)} per token</span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span className="text-wp-muted">Platform fee</span>
                    <span>{terms.platformFeePercent}%</span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span className="text-wp-muted">Lifetime unlocks</span>
                    <span>{fmtInt(stats.lifetime.unlocks)}</span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span className="text-wp-muted">Lifetime earnings</span>
                    <span>{fmtUsd(stats.lifetime.netUsd)}</span>
                  </li>
                </ul>
                <p className="text-wp-muted text-xs mt-3">
                  Earnings = tokens spent on your content × token value × (1 − platform fee). Reader identities are
                  never shared — you see every unlock and click, never who it was.
                </p>
              </Card>
            </div>

            {/* Top content */}
            <Card>
              <SectionTitle sub="Ranked by unlocks in the selected period.">Top content</SectionTitle>
              {stats.topContent.length > 0 ? (
                <TopContentTable topContent={stats.topContent} />
              ) : (
                <p className="text-wp-muted text-sm">No unlocks in this period yet.</p>
              )}
            </Card>

            {/* Recent unlocks */}
            <Card>
              <SectionTitle sub="The latest reader unlocks of your content, anonymized.">Recent unlocks</SectionTitle>
              {transactions && transactions.items.length > 0 ? (
                <>
                  <RecentUnlocksTable items={transactions.items} />
                  <p className="text-wp-muted text-xs mt-3">
                    Showing {transactions.items.length} of {fmtInt(transactions.total)} total unlocks.
                  </p>
                </>
              ) : (
                <p className="text-wp-muted text-sm">No unlocks recorded yet.</p>
              )}
            </Card>
          </div>
        </>
      )}

      {tab === 'overview' && <ApiKeySection publisher={publisher} setPublisher={setPublisher} />}
    </div>
  );
};

export default PublisherDashboard;
